import * as cartConstants from './cartConstants';
import * as cartUtils from './cartUtils';
import * as metaStateUtils from '../../../store/util/metaStateUtils';

import {contentConstants} from '../Content';


/**
 * @param state {object} Global state
 * @return {object} Domain state
 */
const getDomainState = state => {

    return state.cart;
};


/**
 * @param state {object} Global state
 * @return {object} Domain carts state
 */
const getCarts = state => {

    return getDomainState(state)
        .carts;
};


/**
 * @param state {object} Global state
 * @returns {Boolean} True if carts are loaded; false if not
 */
export const areCartsLoading = state => {

    const carts = getCarts(state);

    return metaStateUtils.isLoading(carts);
};


/**
 * @param state {object} Global state
 * @returns {Boolean} True if carts are loaded; false if not
 */
export const areCartsLoaded = state => {

    const carts = getCarts(state);

    return metaStateUtils.isLoaded(carts);
};


/**
 * @param state {object} Global state
 * @return {object} Active cart
 */
export const getActiveCart = state => {

    const activeCart = Object.values(getCarts(state))
        .find(cart => cart.isActive === true);

    return activeCart || {};
};


/**
 * @param state {object} Global state
 * @return {string} Total items in the cart
 */
export const getActiveCartId = state => {

    return getActiveCart(state)
        .id;
};


/**
 * @param state {object} Global state
 * @return {number} Total items in the cart
 */
export const getActiveCartTotalItems = state => {

    return getActiveCart(state)
        .totalItems;
};


/**
 * @param state {object} Global state
 * @param cartId {string} Cart id
 * @return {object|null} Cart or null if not existing
 */
export const getCart = (state, cartId) => {

    return getCarts(state)[cartId] || null;
};


/**
 * @param state {object} Global state
 * @param cartId {string} Cart id
 * @return {boolean} True if cart details have been loaded; false if not
 */
export const isCartLoaded = (state, cartId) => {

    const cart = getCart(state, cartId);

    return !!(cart && cart.items);
};


/**
 * @param state {object} Global state
 * @param cartId {string} Cart id
 * @param itemId {string} Item id to get item for
 * @return {object|null} Cart item or null
 */
export const getCartItem = (state, cartId, itemId) => {

    const cart = getCart(state, cartId);
    if (!cart) {

        return null;
    }

    return cart.items.find(cartItem => cartItem.itemId === itemId);
};


/**
 * @param state {object} Global state
 * @param cartId {string} Cart id
 * @param itemData {object} Item data with type and referenced id
 * @return {object|null} Cart item or null
 */
export const getCartItemForItemData = (state, cartId, itemData) => {

    const itemId = cartUtils.itemIdFromData(itemData);

    return getCartItem(state, cartId, itemId);
};


/**
 * @param state {object} Global state
 * @param itemId {string} Item id
 */
export const isItemInActiveCart = (state, itemId) => {

    const activeCart = getActiveCartId(state);

    return getCartItem(state, activeCart, itemId);
};


/**
 * @param state {object} Global state
 * @param itemData {object} Item data with type and referenced id
 */
export const isItemInActiveCartForItemData = (state, itemData) => {

    const itemId = cartUtils.itemIdFromData(itemData);

    return isItemInActiveCart(state, itemId);
};


/**
 * @param state {object} Global state
 * @return {array|string[]} Seller publication ids of items currently in cart
 */
export const getCartSellerPublicationIds = state => {

    const activeCartId = getActiveCartId(state);
    const cart = getCart(state, activeCartId);

    return cart && cart.sellerPublicationIds || [];
};


/**
 * Checks whether a content can be added to the cart:
 * - Checks if content is status 'online'
 * - Checks if the cart already contains different publication (only one publication in checkout atm)
 *
 * @param state {object} Global state
 * @param contentToCheck {object} Content to check
 * @return {object} Check result with status and optional error-code
 */
export const canContentBeAddedToCart = (state, {publicationId, status}) => {

    // status not orderable
    if (contentConstants.CONTENT_STATUS_NOT_ORDERABLE === status) {

        return {
            canAdd: false,
            reason: cartConstants.CART_ADD_TO_CART_CHECK_REASON_STATUS_NOT_ORDERABLE
        };
    }

    const publicationIds = getCartSellerPublicationIds(state);

    // different publication, first exit
    if (publicationIds.length > 0 && !publicationIds.includes(publicationId)) {

        return {
            canAdd: false,
            reason: cartConstants.CART_ADD_TO_CART_CHECK_REASON_DIFFERENT_PUBLICATION
        };
    }

    return {
        canAdd: true,
        reason: null
    };
};
